import { reactive } from 'vue'

// Preview mode for the Purmo Portal's Translations app. The portal loads this site in an iframe
// with ?preview=1 and sends a market's draft texts with postMessage; the calculator shows them
// instead of its built-in texts. Nothing is stored and nothing reaches customers:
// - only the base app (main.js) listens, never the widgets Umbraco embeds;
// - only inside an iframe, only with ?preview=1;
// - only from origins in VITE_PREVIEW_ORIGINS (empty means preview is off);
// - only for the market and locale this page shows.
// In return the calculator tells the portal which text keys it has read, so the portal can list
// only this calculator's texts. It outlines the text the portal highlights (the cell being
// edited) and, more lightly, the text it hovers, and says how often the highlighted text is on
// screen. In edit mode a text can also be edited right here: preview-edit.js asks the portal to
// edit the clicked text, and the portal saves it exactly like a cell in its grid. A key read is
// not always on screen (an old category title, a placeholder never shown), so preview-seen.js
// also tells the portal which keys have been visible at least once. Once, when the portal is
// known, it also tells the portal every key the view can show in any state (preview-manifest.js).
export const PREVIEW_MESSAGE_TYPE = 'preview-strings'
export const PREVIEW_HIGHLIGHT_TYPE = 'preview-highlight'
export const PREVIEW_HOVER_TYPE = 'preview-hover'
export const PREVIEW_USED_KEYS_TYPE = 'preview-used-keys'
export const PREVIEW_HIGHLIGHT_RESULT_TYPE = 'preview-highlight-result'
// Calculator → portal: every key visible on screen at least once since load
export const PREVIEW_SEEN_KEYS_TYPE = 'preview-seen-keys'
// Calculator → portal, once: every key this view can show in any state, in screen order, the
// same keys grouped in sections, and the keys it can show that another view lists
// (previewAllKeysMessage)
export const PREVIEW_ALL_KEYS_TYPE = 'preview-all-keys'
// The sections of preview-all-keys, in the order they are sent (preview-manifest.js says which
// texts go where): the form, its dropdown options, the results, then loading and hint texts
export const PREVIEW_SECTIONS = ['form', 'options', 'results', 'messages']
// Portal → calculator: edit mode on or off, and the answer to a request to edit one text
export const PREVIEW_EDIT_MODE_TYPE = 'preview-edit-mode'
export const PREVIEW_EDIT_OPEN_TYPE = 'preview-edit-open'
// Calculator → portal: start editing a text, what is typed, and Enter or Escape
export const PREVIEW_EDIT_TYPE = 'preview-edit'
export const PREVIEW_EDIT_PHASES = ['start', 'input', 'commit', 'cancel']

// Wrap the highlighted and the hovered text so preview-highlight.js can find exactly where each is
// shown, also when another key has the same wording. Both take no space on screen.
export const HIGHLIGHT_MARK = '\u2063' // invisible separator
export const HOVER_MARK = '\u2062' // invisible times

// In edit mode, and while preview-seen.js looks for the texts on screen, every text read ends in
// an invisible tag naming its key, so a click on the page can tell which text it hit, also among
// several texts in one element. The tag is a number in binary, ZERO WIDTH SPACE for 0 and ZERO
// WIDTH NON-JOINER for 1, between two FUNCTION APPLICATION marks. None of them takes space on
// screen. The tag goes last, so sorting by text is unchanged.
const TAG_EDGE = '\u2061'
const TAG_ZERO = '\u200B'
const TAG_ONE = '\u200C'
const TAG_PATTERN = /\u2061([\u200B\u200C]+)\u2061/g

const MAX_KEYS = 2000
const MAX_TEXT_LENGTH = 2000
const KEY_PATTERN = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/
// Keys that would reach Object.prototype when merged
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor'])
// Rendering reads many texts at once; they go to the portal as one message after this pause
const USED_KEYS_REPORT_DELAY_MS = 200

// The texts received for one locale, as a nested object like the files in strings/, and the keys
// the portal asks to highlight and to show as hovered
export const previewState = reactive({
  locale: null,
  strings: null,
  highlightKey: null,
  hoverKey: null,
  // Whether a click on a text edits it, and the portal's answer to the latest request to edit:
  // { key, value, english, reason }, with value null and a reason when the text cannot be edited
  editMode: false,
  editOpening: null,
})

// Set only while a preview listener runs (startPreviewListener): called with each text key read,
// with the keys found on screen, and with a message for the portal
let recordTextRead = null
let recordTextsSeen = null
let sendToPortal = null
// Whether every text read carries its key tag outside edit mode too (setTagEveryText)
let tagEveryText = false

export function allowedPreviewOrigins(configured) {
  return (configured ?? '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean)
}

function isSafeKey(key) {
  return KEY_PATTERN.test(key) && !key.split('.').some((part) => FORBIDDEN_SEGMENTS.has(part))
}

// Keys in the order they were first tagged; a key's tag is its index here
const taggedKeys = []
const tagIndexes = new Map()

export function keyTag(key) {
  let index = tagIndexes.get(key)
  if (index === undefined) {
    index = taggedKeys.push(key) - 1
    tagIndexes.set(key, index)
  }
  const bits = index.toString(2).replaceAll('0', TAG_ZERO).replaceAll('1', TAG_ONE)
  return `${TAG_EDGE}${bits}${TAG_EDGE}`
}

// The tagged texts in a text, in order: each key and where its tag ends
export function keysTaggedIn(text) {
  const found = []
  for (const match of text.matchAll(TAG_PATTERN)) {
    const index = parseInt(match[1].replaceAll(TAG_ZERO, '0').replaceAll(TAG_ONE, '1'), 2)
    const key = taggedKeys[index]
    if (key) found.push({ key, end: match.index + match[0].length })
  }
  return found
}

// The key of the text at offset in text: the first tag after the offset ends that text
export function keyAtOffset(text, offset) {
  return keysTaggedIn(text).find(({ end }) => end > offset)?.key ?? null
}

// Returns { market, locale, strings } for a well-formed message, otherwise null
export function parsePreviewMessage(data) {
  if (!data || typeof data !== 'object' || data.type !== PREVIEW_MESSAGE_TYPE) return null
  const { market, locale, strings } = data
  if (typeof market !== 'string' || typeof locale !== 'string') return null
  if (!strings || typeof strings !== 'object' || Array.isArray(strings)) return null
  const entries = Object.entries(strings)
  if (entries.length > MAX_KEYS) return null
  const valid = entries.every(
    ([key, text]) => isSafeKey(key) && typeof text === 'string' && text.length <= MAX_TEXT_LENGTH
  )
  return valid ? { market, locale, strings: Object.fromEntries(entries) } : null
}

// Returns { type, market, locale, key } for a well-formed highlight or hover message, otherwise
// null. A null key clears it.
export function parseHighlightMessage(data) {
  if (!data || typeof data !== 'object') return null
  const { type, market, locale, key } = data
  if (type !== PREVIEW_HIGHLIGHT_TYPE && type !== PREVIEW_HOVER_TYPE) return null
  if (typeof market !== 'string' || typeof locale !== 'string') return null
  if (key !== null && (typeof key !== 'string' || !isSafeKey(key))) return null
  return { type, market, locale, key }
}

// Returns { enabled } for a well-formed edit mode message, otherwise null
export function parseEditModeMessage(data) {
  if (!data || typeof data !== 'object' || data.type !== PREVIEW_EDIT_MODE_TYPE) return null
  const { market, locale, enabled } = data
  if (typeof market !== 'string' || typeof locale !== 'string') return null
  if (typeof enabled !== 'boolean') return null
  return { type: PREVIEW_EDIT_MODE_TYPE, market, locale, enabled }
}

// Returns the portal's answer to an edit request: the text to start from and the English text, or
// a null value and the reason the text cannot be edited. Otherwise null.
export function parseEditOpenMessage(data) {
  if (!data || typeof data !== 'object' || data.type !== PREVIEW_EDIT_OPEN_TYPE) return null
  const { market, locale, key, value, english, reason } = data
  if (typeof market !== 'string' || typeof locale !== 'string') return null
  if (typeof key !== 'string' || !isSafeKey(key)) return null
  const isText = (text) => typeof text === 'string' && text.length <= MAX_TEXT_LENGTH
  if (value !== null && !isText(value)) return null
  if (english !== null && !isText(english)) return null
  if (reason !== null && !isText(reason)) return null
  if (value === null && reason === null) return null
  return { type: PREVIEW_EDIT_OPEN_TYPE, market, locale, key, value, english, reason }
}

// Tells the portal what happens to a text edited in the preview. Start asks to edit it; input
// and commit carry the text, cancel puts back what it was.
export function sendEdit(key, phase, value = null) {
  if (!isSafeKey(key) || !PREVIEW_EDIT_PHASES.includes(phase)) return
  const carriesText = phase === 'input' || phase === 'commit'
  if (carriesText && (typeof value !== 'string' || value.length > MAX_TEXT_LENGTH)) return
  sendToPortal?.({ type: PREVIEW_EDIT_TYPE, key, phase, value: carriesText ? value : null })
}

// Tags every text read, also outside edit mode, so the texts on screen can be told apart by key.
// Set before the calculator renders: texts already rendered keep what they had.
export function setTagEveryText(enabled) {
  tagEveryText = enabled
}

// Adds keys found on screen to the keys the portal hears about as seen; they are never removed
export function recordSeenKeys(keys) {
  recordTextsSeen?.(keys)
}

// Tells the portal on how many visible elements the highlighted key is shown; 0 means it is not
// on screen right now, e.g. inside a closed drawer
export function reportHighlightResult(key, shown) {
  sendToPortal?.({ type: PREVIEW_HIGHLIGHT_RESULT_TYPE, key, shown })
}

// { 'app.categories.panels': 'Paneler' } → { app: { categories: { panels: 'Paneler' } } }
export function unflattenStrings(flat) {
  const nested = {}
  for (const [key, text] of Object.entries(flat)) {
    const parts = key.split('.')
    let node = nested
    parts.slice(0, -1).forEach((part) => {
      if (typeof node[part] !== 'object' || node[part] === null) node[part] = {}
      node = node[part]
    })
    node[parts[parts.length - 1]] = text
  }
  return nested
}

// A copy of base with overrides laid over it; base is never changed
export function mergeStrings(base, overrides) {
  const merged = { ...base }
  for (const [key, value] of Object.entries(overrides)) {
    const current = merged[key]
    merged[key] =
      value && typeof value === 'object' && current && typeof current === 'object'
        ? mergeStrings(current, value)
        : value
  }
  return merged
}

// The same proxy for the same object and key prefix, so a strings object passed as a prop keeps
// its identity between renders
const recordingProxies = new WeakMap()

// A read-only view of strings that reports the dot key of every text read through it, and wraps
// the highlighted text in HIGHLIGHT_MARK and the hovered one in HOVER_MARK. The texts reach the screen only by being read, so the
// keys read are the texts this calculator shows, including ones looked up dynamically like
// strings.app.categories[value].
export function recordingStrings(strings, onRead, prefix = '') {
  let byPrefix = recordingProxies.get(strings)
  if (!byPrefix) {
    byPrefix = new Map()
    recordingProxies.set(strings, byPrefix)
  }
  const cached = byPrefix.get(prefix)
  if (cached && cached.onRead === onRead) return cached.proxy

  const proxy = new Proxy(strings, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver)
      // Symbols and Vue's internal flags are not text keys
      if (typeof property !== 'string' || !Object.hasOwn(target, property)) return value
      const key = prefix ? `${prefix}.${property}` : property
      if (typeof value === 'string') {
        onRead(key)
        // Reading the state here makes whatever renders this text re-render when it changes.
        // The highlight wins when the same text is also hovered.
        const text = previewState.editMode || tagEveryText ? `${value}${keyTag(key)}` : value
        if (previewState.highlightKey === key) return `${HIGHLIGHT_MARK}${text}${HIGHLIGHT_MARK}`
        if (previewState.hoverKey === key) return `${HOVER_MARK}${text}${HOVER_MARK}`
        return text
      }
      if (value && typeof value === 'object') return recordingStrings(value, onRead, key)
      return value
    },
    set() {
      return false
    },
    deleteProperty() {
      return false
    },
  })
  byPrefix.set(prefix, { onRead, proxy })
  return proxy
}

// Used by getStrings: the built-in texts, with the preview texts on top for the previewed locale.
// While a preview listener runs, every text read is recorded for the portal.
export function withPreviewStrings(base, locale) {
  const strings =
    previewState.strings && previewState.locale === locale
      ? mergeStrings(base, previewState.strings)
      : base
  return recordTextRead ? recordingStrings(strings, recordTextRead) : strings
}

// The outline colour of the portal's theme, as RGB parts for rgba() in the styles that
// preview-highlight.js and preview-edit.js inject: the portal's blue for ?theme=stupid, Purmo's
// orange otherwise. Only these names are read; the URL comes from another site.
const PREVIEW_THEME_RGB = { purmo: '232, 89, 12', stupid: '10, 132, 255' }

export function previewThemeRgb(theme) {
  return Object.hasOwn(PREVIEW_THEME_RGB, theme)
    ? PREVIEW_THEME_RGB[theme]
    : PREVIEW_THEME_RGB.purmo
}

// The preview-all-keys message, without market and locale, from a view's listing
// (previewAllKeys in preview-manifest.js): listed is [{ key, section }] in screen order, elsewhere
// the keys another view lists.
// - keys: the listed keys, in screen order. Portals that read only keys keep working.
// - sections: [{ id, keys }], the same keys grouped, in PREVIEW_SECTIONS order, empty ones left
//   out. Every key is in exactly one section.
// - elsewhere: keys this view can show but another tab of the portal lists, never one in keys.
// Only well-formed keys, each once, with a known section, at most MAX_KEYS in keys and in
// elsewhere each.
export function previewAllKeysMessage({ listed = [], elsewhere = [] }) {
  const sectionOfKey = new Map()
  for (const entry of listed) {
    if (sectionOfKey.size >= MAX_KEYS) break
    const { key, section } = entry ?? {}
    if (typeof key !== 'string' || !isSafeKey(key) || sectionOfKey.has(key)) continue
    if (!PREVIEW_SECTIONS.includes(section)) continue
    sectionOfKey.set(key, section)
  }
  const keys = [...sectionOfKey.keys()]
  const sections = PREVIEW_SECTIONS.map((id) => ({
    id,
    keys: keys.filter((key) => sectionOfKey.get(key) === id),
  })).filter((section) => section.keys.length > 0)
  const elsewhereKeys = [...new Set(elsewhere)]
    .filter((key) => typeof key === 'string' && isSafeKey(key) && !sectionOfKey.has(key))
    .slice(0, MAX_KEYS)
  return { type: PREVIEW_ALL_KEYS_TYPE, keys, sections, elsewhere: elsewhereKeys }
}

// Starts listening when this page is a preview frame; returns a stop function (or null).
// listAllKeys({ market, locale }) returns the view's listing for previewAllKeysMessage
// (preview-manifest.js); main.js passes it in, so the manifest never reaches the widget bundles
// that import this file.
export function startPreviewListener({
  win = window,
  allowedOrigins,
  resolveLocale,
  listAllKeys = null,
}) {
  const params = new URLSearchParams(win.location.search)
  const isFramed = win.parent && win.parent !== win
  if (params.get('preview') !== '1' || !isFramed || allowedOrigins.length === 0) return null
  win.document.documentElement.style.setProperty('--pv-rgb', previewThemeRgb(params.get('theme')))
  const market = params.get('market') ?? ''
  const locale = resolveLocale(params.get('lang'), market)
  // Held until the portal is known, then sent once: the list does not change while the page runs
  const allKeysMessage = listAllKeys ? previewAllKeysMessage(listAllKeys({ market, locale })) : null

  // Keys go back only to the origin the portal's own messages came from, never '*'; until the
  // first message arrives they wait here
  const usedKeys = new Set()
  let portalOrigin = null
  let reportTimer
  sendToPortal = (message) => {
    if (portalOrigin) win.parent.postMessage({ ...message, market, locale }, portalOrigin)
  }
  // In first-read order, which is close to the order the texts appear on screen
  const reportUsedKeys = () => sendToPortal({ type: PREVIEW_USED_KEYS_TYPE, keys: [...usedKeys] })
  const scheduleReport = () => {
    clearTimeout(reportTimer)
    reportTimer = setTimeout(reportUsedKeys, USED_KEYS_REPORT_DELAY_MS)
  }
  recordTextRead = (key) => {
    if (usedKeys.has(key) || usedKeys.size >= MAX_KEYS) return
    usedKeys.add(key)
    scheduleReport()
  }

  // The keys seen only grow, so a new size means a new key. Nothing goes out before the first key
  // is seen: the portal keeps its fallback, the keys read, until then.
  const seenKeys = new Set()
  let seenKeysSent = 0
  let seenReportTimer
  const reportSeenKeys = () => {
    if (!portalOrigin || seenKeys.size === seenKeysSent) return
    seenKeysSent = seenKeys.size
    sendToPortal({ type: PREVIEW_SEEN_KEYS_TYPE, keys: [...seenKeys] })
  }
  const scheduleSeenReport = () => {
    clearTimeout(seenReportTimer)
    seenReportTimer = setTimeout(reportSeenKeys, USED_KEYS_REPORT_DELAY_MS)
  }
  recordTextsSeen = (keys) => {
    const sizeBefore = seenKeys.size
    for (const key of keys) {
      if (seenKeys.size >= MAX_KEYS) break
      if (typeof key === 'string' && isSafeKey(key)) seenKeys.add(key)
    }
    if (seenKeys.size > sizeBefore) scheduleSeenReport()
  }

  const onMessage = (event) => {
    if (!allowedOrigins.includes(event.origin) || event.source !== win.parent) return
    const message =
      parsePreviewMessage(event.data) ??
      parseHighlightMessage(event.data) ??
      parseEditModeMessage(event.data) ??
      parseEditOpenMessage(event.data)
    if (!message || message.market !== market || message.locale !== locale) return
    if (!portalOrigin) {
      portalOrigin = event.origin
      if (allKeysMessage) sendToPortal(allKeysMessage)
      scheduleReport()
      scheduleSeenReport()
    }
    if ('strings' in message) {
      previewState.locale = message.locale
      previewState.strings = unflattenStrings(message.strings)
    } else if (message.type === PREVIEW_HOVER_TYPE) {
      previewState.hoverKey = message.key
    } else if (message.type === PREVIEW_EDIT_MODE_TYPE) {
      previewState.editMode = message.enabled
    } else if (message.type === PREVIEW_EDIT_OPEN_TYPE) {
      const { key, value, english, reason } = message
      previewState.editOpening = { key, value, english, reason }
    } else {
      previewState.highlightKey = message.key
    }
  }
  win.addEventListener('message', onMessage)
  return () => {
    win.removeEventListener('message', onMessage)
    clearTimeout(reportTimer)
    clearTimeout(seenReportTimer)
    recordTextRead = null
    recordTextsSeen = null
    sendToPortal = null
    previewState.editMode = false
    previewState.editOpening = null
  }
}
